'use client';

import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useDirection } from '@radix-ui/react-direction';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useMemo } from 'react';
import * as RechartsPrimitive from 'recharts';
import type { AxisChartOptions } from '../types';
import { BarChartTooltip, type BarChartTooltipProps } from './BarChartTooltip';
import { TruncatedXAxisTick } from './TruncatedXAxisTick';
import { TruncatedYAxisLabel } from './TruncatedYAxisLabel';
import { TruncatedYAxisTick } from './TruncatedYAxisTick';
import { calculateNiceInterval, calculateYAxisConfig } from './utils';

/**
 * Data item for a bar chart.
 * Additional properties can be added and will be passed through to tooltips.
 */
export interface BarChartDataItem {
  /** The main label for this data point (displayed on the x-axis) */
  name: string;
  /** Informational label placed underneath the main label */
  subLabel?: string;
  /** The numeric value for this data point */
  value: number;
  /** Additional properties passed through to tooltip */
  [key: string]: unknown;
}

/**
 * Localizable text strings for BarChart accessibility descriptions.
 */
export interface BarChartText {
  /** Screen reader description when chart has no data. Default: "Bar chart has no data." */
  noData?: string;
  /** Default chart description when no axes are labeled. Default: "Bar chart." */
  descriptionDefault?: string;
  /** Keyboard navigation hint. Default: "Use left and right arrow keys to navigate between data points." */
  keyboardNavigationHint?: string;
  /** Additional description appended to the chart's accessible description. Overrides the default/pre-built descriptions */
  description?: string;
  /** text for "x-axis" */
  xAxis?: string;
  /** text for "y-axis" */
  yAxis?: string;
  /** Template for chart with both axes. Use {xAxis} and {yAxis} placeholders. Default: "Bar chart showing {xAxis} on the x-axis and {yAxis} on the y-axis." */
  descriptionWithBothAxes?: (xAxis: string, yAxis: string) => string;
  /** Template for chart with x-axis only. Use {xAxis} placeholder. Default: "Bar chart with {xAxis} on the x-axis." */
  descriptionWithXAxis?: (xAxis: string) => string;
  /** Template for chart with y-axis only. Use {yAxis} placeholder. Default: "Bar chart with {yAxis} on the y-axis." */
  descriptionWithYAxis?: (yAxis: string) => string;
}

export type BarChartProps = React.ComponentProps<typeof RechartsPrimitive.BarChart> &
  VariantProps<typeof barChartVariants> & {
    /** The data to display in the bar chart */
    data: BarChartDataItem[];
    /** The key in the data to use for the bar values. Defaults to 'value'. */
    dataKey?: string;
    /** Chart configuration options */
    options?: AxisChartOptions<BarChartTooltipProps>;
    /** Accessible description ID(s) for the chart, used for screen readers */
    'aria-describedby'?: string;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: BarChartText;
  };

export const barChartVariants = cva(['w-full', 'min-h-40', 'aspect-video', '[&_*]:outline-none', 'focus-within-ring']);

const barChartBarVariants = cva(['fill-chart-series-1'], {
  variants: {
    active: {
      true: 'fill-chart-series-1-hover',
      false: '',
    },
  },
  defaultVariants: {
    active: false,
  },
});

const chartAxisVariants = cva(['[&_line]:stroke-chart-axis']);

const chartAxisLabelVariants = cva(['fill-chart-axis-label text-xs']);

const chartAxisTickLabelVariants = cva(['fill-chart-axis-tick-label text-xs']);

const chartGridVariants = cva(['stroke-chart-grid']);

export const BarChart = forwardRef<SVGSVGElement, BarChartProps>(
  (
    {
      data,
      dataKey = 'value',
      className,
      options,
      'aria-describedby': externalAriaDescribedBy,
      text = {},
      layout = 'horizontal',
      ...props
    },
    ref,
  ) => {
    const dir = useDirection();
    const isRtl = dir === 'rtl';
    const internalDescId = useId();
    const ariaDescribedBy = externalAriaDescribedBy ? `${externalAriaDescribedBy} ${internalDescId}` : internalDescId;

    // Extract text with defaults
    const {
      noData = 'Bar chart has no data.',
      descriptionDefault = 'Bar chart.',
      keyboardNavigationHint = 'Use left and right arrow keys to navigate between data points.',
      description = '',
      xAxis = 'X-axis',
      yAxis = 'Y-axis',
      descriptionWithBothAxes = (xAxisLabel: string, yAxisLabel: string) =>
        `Bar chart showing ${xAxisLabel} on the x-axis and ${yAxisLabel} on the y-axis.`,
      descriptionWithXAxis = (xAxisLabel: string) => `Bar chart with ${xAxisLabel} on the x-axis.`,
      descriptionWithYAxis = (yAxisLabel: string) => `Bar chart with ${yAxisLabel} on the y-axis.`,
    } = text;

    // Extract options with defaults
    const xAxisLabelOption = options?.axes?.x?.label || '';
    const yAxisLabelOption = options?.axes?.y?.label || '';
    const providedYAxisMax = options?.axes?.y?.max;
    const allowDecimals = options?.axes?.y?.allowDecimals ?? true;
    const tooltipRender = options?.tooltip?.render;

    // Swap axis labels for vertical layout (axes swap roles)
    const xAxisLabel = layout === 'vertical' ? yAxisLabelOption : xAxisLabelOption;
    const yAxisLabel = layout === 'vertical' ? xAxisLabelOption : yAxisLabelOption;

    const maxDataValue = data.length > 0 ? Math.max(...data.map((d) => d.value)) : 0;
    const { yAxisMax, yAxisTicks } = calculateYAxisConfig({
      maxDataValue,
      yAxisMax: providedYAxisMax,
      allowDecimals,
    });

    // For vertical layout, use less headroom (10% instead of full interval)
    const xAxisMaxVertical = providedYAxisMax ?? Math.ceil(maxDataValue * 1.1);

    // Calculate dynamic Y-axis width for vertical layout based on longest label
    const yAxisWidth = useMemo<number>(() => {
      if (layout !== 'vertical' || data.length === 0) return 50;

      const maxLabelLength = Math.max(...data.map((d) => String(d.name).length));
      // Approximate 7 pixels per character + 20px padding
      const estimatedWidth = Math.min(Math.max(maxLabelLength * 7 + 20, 100), 300);
      return estimatedWidth;
    }, [data]);

    const hasSubLabels = data.some((d) => d.subLabel);

    // Calculate x-axis tick values for accessibility
    const xAxisTickValues = data.map((d) => d.name).join(', ');

    // Calculate y-axis ticks for accessibility (even when allowDecimals is true and yAxisTicks is undefined)
    const getAccessibleYAxisTicks = (): number[] => {
      if (yAxisTicks) return yAxisTicks;
      // Calculate ticks the same way Recharts would auto-generate them
      const interval = calculateNiceInterval(yAxisMax);
      const ticks: number[] = [];
      for (let tick = 0; tick <= yAxisMax; tick += interval) {
        ticks.push(tick);
      }
      return ticks;
    };

    const yAxisTickValues = getAccessibleYAxisTicks().join(', ');

    const buildAccessibleDescription = () => {
      const parts: string[] = [];

      // Empty data - short circuit with simple message
      if (data.length === 0) {
        if (description) {
          parts.push(description.endsWith('.') ? description : `${description}.`);
        }
        parts.push(noData);
        return parts.join(' ');
      }

      // Use description if provided, otherwise construct from axis labels
      if (description) {
        parts.push(description);
      } else if (xAxisLabel && yAxisLabel) {
        parts.push(descriptionWithBothAxes(xAxisLabel, yAxisLabel));
      } else if (xAxisLabel) {
        parts.push(descriptionWithXAxis(xAxisLabel));
      } else if (yAxisLabel) {
        parts.push(descriptionWithYAxis(yAxisLabel));
      } else {
        parts.push(descriptionDefault);
      }

      // X-axis tick values
      if (xAxisTickValues) {
        const description = xAxisLabel ? `${xAxis}: ${xAxisLabel}. Labels: ${xAxisTickValues}.` : `${xAxis} labels: ${xAxisTickValues}.`;

        parts.push(description);
      }

      // Y-axis tick values
      if (yAxisTickValues) {
        const description = yAxisLabel ? `${yAxis}: ${yAxisLabel}. Labels: ${yAxisTickValues}.` : `${yAxis} labels: ${yAxisTickValues}.`;

        parts.push(description);
      }

      // Keyboard navigation hint
      parts.push(keyboardNavigationHint);

      return parts.join(' ');
    };

    const accessibleDescription = buildAccessibleDescription();

    return (
      <>
        <span id={internalDescId} className="sr-only">
          {accessibleDescription}
        </span>
        <RechartsPrimitive.BarChart
          ref={ref}
          data={data}
          accessibilityLayer
          aria-describedby={ariaDescribedBy}
          className={cn(barChartVariants(), className)}
          responsive
          layout={layout}
          margin={{
            top: 10,
            right: 0,
            left: 20,
            bottom: layout === 'horizontal' && hasSubLabels ? 50 : 30,
          }}
          {...props}
        >
          <RechartsPrimitive.XAxis
            dataKey={layout === 'vertical' ? dataKey : 'name'}
            type={layout === 'vertical' ? 'number' : 'category'}
            className={cn(chartAxisVariants())}
            tick={
              layout === 'vertical'
                ? { className: cn(chartAxisTickLabelVariants()) }
                : (props) => <TruncatedXAxisTick {...props} data={data} />
            }
            tickLine={false}
            axisLine={false}
            interval={layout === 'vertical' ? undefined : 0}
            {...(layout === 'vertical' ? { domain: [0, xAxisMaxVertical], allowDecimals } : {})}
          >
            {xAxisLabel && (
              <RechartsPrimitive.Label className={cn(chartAxisLabelVariants())} value={xAxisLabel} offset={-20} position="insideBottom" />
            )}
          </RechartsPrimitive.XAxis>
          <RechartsPrimitive.YAxis
            dataKey={layout === 'vertical' ? 'name' : undefined}
            className={cn(chartAxisVariants())}
            type={layout === 'vertical' ? 'category' : 'number'}
            tick={
              layout === 'vertical'
                ? (props) => <TruncatedYAxisTick {...props} data={data} />
                : { className: cn(chartAxisTickLabelVariants()), textAnchor: isRtl ? 'start' : 'end' }
            }
            tickLine={false}
            axisLine={false}
            {...(layout === 'vertical' ? { interval: 0 } : { domain: [0, yAxisMax], allowDecimals, ticks: yAxisTicks })}
            width={yAxisWidth}
          >
            {yAxisLabel && <RechartsPrimitive.Label value={yAxisLabel} position="insideLeft" content={TruncatedYAxisLabel} />}
          </RechartsPrimitive.YAxis>
          <RechartsPrimitive.CartesianGrid
            horizontal={layout === 'horizontal'}
            vertical={layout === 'vertical'}
            strokeDasharray="3 3"
            className={cn(chartGridVariants())}
          />
          <RechartsPrimitive.Bar
            className={barChartBarVariants()}
            dataKey={dataKey}
            activeBar={{ className: cn(barChartBarVariants({ active: true })) }}
            maxBarSize={layout === 'vertical' ? 40 : undefined}
          />
          <RechartsPrimitive.Tooltip
            isAnimationActive={false}
            cursor={false}
            content={<BarChartTooltip contentOverride={tooltipRender} />}
          />
        </RechartsPrimitive.BarChart>
      </>
    );
  },
);

BarChart.displayName = 'BarChart';
